import EncaminhamentoExternoDetalhesPanel from '@atendimento/detail/soap/plano/encaminhamento-externo/list/detalhes/EncaminhamentoExternoDetalhesPanel'
import EncaminhamentoExternoEspecializadoDetalhesPanel from '@atendimento/detail/soap/plano/encaminhamento-externo/list/detalhes/EncaminhamentoExternoEspecializadoDetalhesPanel'
import { StatusClassificacaoRiscoEncaminhamento } from '@atendimento/detail/soap/plano/encaminhamento-externo/list/StatusClassificacaoRiscoEncaminhamento'
import {
  EncaminhamentoExternoEspecializadoQueryModel,
  EncaminhamentoExternoQueryModel,
  EncaminhamentoExternoRowModel,
  tipoEncaminhamentoExternoEnumRecord,
} from '@atendimento/detail/soap/plano/encaminhamento-externo/model-encaminhamentoExterno'
import {
  createEncaminhamentosEspecializadosFromDatabase,
  encaminhamentosFromDatabase,
} from '@atendimento/detail/soap/plano/encaminhamento-externo/util/mergeAndFilterEncaminhamentos'
import { css } from '@emotion/react'
import { HFlow, TableFooter, Text } from 'bold-ui'
import { AccordionDataTable } from 'components/accordion/accordion-data-table/AccordionDataTable'
import { useAccordionControl } from 'components/accordion/useAccordionControl'
import { DateTime } from 'components/date'
import { usePagination } from 'components/table/usePagination'
import theme from 'config/theme'
import { addDays } from 'date-fns'
import { useEncaminhamentosExternosEspecializadosQuery, useEncaminhamentosExternosQuery } from 'graphql/hooks.generated'
import { TipoAcompanhamentoEnum, TipoEncaminhamentoExternoEnum } from 'graphql/types.generated'
import moment from 'moment'
import React from 'react'

import { AcompanhamentoCard } from '../../components/AcompanhamentoCard'
import { DIAS_GESTACAO_LIMITE } from '../../puerperio/model-acompanhamentoPuerperio'

const mergeAndFilterEncaminhamentos = (
  encaminhamentosEspecializadosDatabase: ReadonlyArray<EncaminhamentoExternoEspecializadoQueryModel>,
  encaminhamentosDatabase: ReadonlyArray<EncaminhamentoExternoQueryModel>,
  dataInicioGestacao: Date,
  dataLimiteGestacao: Date
): EncaminhamentoExternoRowModel[] => {
  const encaminhamentosEspecializadosDatabaseConvertidos =
    encaminhamentosEspecializadosDatabase?.map(createEncaminhamentosEspecializadosFromDatabase) ?? []

  const encaminhamentosDatabaseConvertidos = encaminhamentosDatabase?.map(encaminhamentosFromDatabase) ?? []

  return [...encaminhamentosEspecializadosDatabaseConvertidos, ...encaminhamentosDatabaseConvertidos]
    .filter((encaminhamento) => {
      const dataAtendimento = moment(encaminhamento.atendimentoProfissional.iniciadoEm)

      const isAfterStart = dataAtendimento.isSameOrAfter(dataInicioGestacao, 'date')
      const isBeforeEnd = dataAtendimento.isSameOrBefore(dataLimiteGestacao, 'date')

      return isAfterStart && isBeforeEnd
    })
    .sort((a, b) => b.atendimentoProfissional.iniciadoEm - a.atendimentoProfissional.iniciadoEm)
}

interface EncaminhamentosAntigosGestacaoCardProps {
  prontuarioId: ID
  dataInicioGestacao: Date
}

export function EncaminhamentosAntigosGestacaoCard(props: Readonly<EncaminhamentosAntigosGestacaoCardProps>) {
  const { prontuarioId, dataInicioGestacao } = props

  const { isExpanded, handleAccordionItemClick, resetExpandedItems, ...accordionProps } = useAccordionControl({
    allowMultipleExpanded: true,
  })

  const {
    data: { encaminhamentos },
    loading: loadingEncaminhamentos,
  } = useEncaminhamentosExternosQuery({
    variables: {
      prontuarioId,
    },
  })

  const {
    data: { encaminhamentosEspecializados },
    loading: loadingEncaminhamentosEspecializados,
  } = useEncaminhamentosExternosEspecializadosQuery({
    variables: {
      prontuarioId,
    },
  })

  const loading = loadingEncaminhamentos || loadingEncaminhamentosEspecializados

  const dataLimiteGestacao = addDays(dataInicioGestacao, DIAS_GESTACAO_LIMITE)

  const encaminhamentosExibir = mergeAndFilterEncaminhamentos(
    encaminhamentosEspecializados?.content,
    encaminhamentos,
    dataInicioGestacao,
    dataLimiteGestacao
  )

  const { paginatedItems, tableProps } = usePagination({
    items: encaminhamentosExibir,
    initialPageSize: 8,
    sizeOptions: [8],
    onChange: resetExpandedItems,
  })

  const mostrarDetalhesEncaminhamentoEspecializado = (row: EncaminhamentoExternoRowModel) =>
    row.tipoEncaminhamento === TipoEncaminhamentoExternoEnum.SERVICO_ESPECIALIZADO

  function renderAccordionPanel(row: EncaminhamentoExternoRowModel) {
    if (mostrarDetalhesEncaminhamentoEspecializado(row)) {
      return <EncaminhamentoExternoEspecializadoDetalhesPanel encaminhamentoId={row.id} isRegistradoAgora={false} />
    }
    return (
      <EncaminhamentoExternoDetalhesPanel
        atendimentoProfissionalId={row.atendimentoProfissional.id}
        isRegistradoAgora={false}
      />
    )
  }

  return (
    <AcompanhamentoCard
      id='encaminhamentos'
      title='Encaminhamentos'
      tipoAcompanhamento={TipoAcompanhamentoEnum.PRE_NATAL}
      loading={loading}
      placeholder='Nenhum registro registrado'
      justifyHeaderContent='space-between'
    >
      <AccordionDataTable<EncaminhamentoExternoRowModel>
        {...accordionProps}
        isExpanded={isExpanded}
        handleAccordionItemClick={handleAccordionItemClick}
        rows={paginatedItems}
        columns={[
          {
            name: 'data',
            header: 'Data do encaminhamento',
            render: renderData,
            size: 3,
          },
          {
            name: 'risco',
            header: 'Risco',
            render: renderRisco,
            style: classes.riscoColumn,
            size: 1,
          },
          {
            name: 'especialidade',
            header: 'Tipo de encaminhamento',
            render: renderTipoEncaminhamento,
            style: classes.especialidadeColumn,
            size: 4,
          },
        ]}
        components={{
          AccordionPanel: ({ row }) => renderAccordionPanel(row),
        }}
      />
      <TableFooter {...tableProps} style={classes.tableFooter} />
    </AcompanhamentoCard>
  )
}

const renderData = (row: EncaminhamentoExternoRowModel) => {
  return <DateTime value={row.atendimentoProfissional?.iniciadoEm} format='DD/MM/YYYY' />
}

const renderRisco = (row: EncaminhamentoExternoRowModel) => {
  return (
    <HFlow alignItems='center' justifyContent='center'>
      {row.classificacaoRiscoEncaminhamento ? (
        <StatusClassificacaoRiscoEncaminhamento status={row.classificacaoRiscoEncaminhamento} />
      ) : (
        <Text>-</Text>
      )}
    </HFlow>
  )
}

const renderTipoEncaminhamento = (row: EncaminhamentoExternoRowModel) => {
  return row.tipoEncaminhamento !== TipoEncaminhamentoExternoEnum.SERVICO_ESPECIALIZADO ? (
    <Text>{tipoEncaminhamentoExternoEnumRecord[row.tipoEncaminhamento].descricao}</Text>
  ) : (
    <React.Fragment>
      <Text fontWeight='bold'>{tipoEncaminhamentoExternoEnumRecord.SERVICO_ESPECIALIZADO.descricao}</Text>
      <Text> - {row.especialidadeSisreg?.descricao.capitalize()}</Text>
    </React.Fragment>
  )
}

const classes = {
  especialidadeColumn: css`
    ${theme.breakpoints.down('lg')} {
      word-break: break-word;
    }
  `,

  riscoColumn: css`
    text-align: center;
  `,

  tableFooter: css`
    border-top-width: 0;
  `,
}
